import Vue from 'vue';
import VueRouter from 'vue-router';
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta';
// Adds a loading bar at the top during page loads.
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import NProgress from 'nprogress/nprogress';
import BootstrapVue from 'bootstrap-vue';
import Cloudinary, { CldImage, CldTransformation } from 'cloudinary-vue';
import OpenIndicator from '@components/inputs/open-indicator.vue';
import VueBreadcrumbs from 'vue-2-breadcrumbs';
import VueLazyload from 'vue-lazyload';
import vSelect from 'vue-select';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
import 'video.js/dist/video-js.css';
import Vuelidate from 'vuelidate';
import VueObserveVisibility from 'vue-observe-visibility';
import vueCountryRegionSelect from 'vue-country-region-select';
import { VueMaskFilter } from 'v-mask';
import store from '@state/store';
import routes from './routes';
const VideoBackground = () =>
  import(
    /* webpackChunkName: "VideoBackground" */ 'vue-responsive-video-background-player'
  );
Vue.use(Vuelidate);
Vue.use(VueRouter);
Vue.use(BootstrapVue);
Vue.use(VueBreadcrumbs);
Vue.use(vueCountryRegionSelect);
Vue.use(VueObserveVisibility);
Vue.filter('VMask', VueMaskFilter);
Vue.component('VueSlider', VueSlider);
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
});

Vue.use(VueLazyload, {
  preLoad: 1.3,
  // error: 'dist/error.png',
  // loading: 'dist/loading.gif',
  attempt: 1,
});

Vue.use(Cloudinary, {
  configuration: { cloudName: 'menud' },
  components: {
    CldImage,
    CldTransformation,
  },
});

vSelect.props.components.default = () => ({ OpenIndicator });
Vue.component('v-select', vSelect);

Vue.component('video-background', VideoBackground);

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // If this isn't an initial page load...
  if (
    store.getters['workout/hasUnsavedChanges'] ||
    store.getters['exercise/hasUnsavedExerciseChanges'] ||
    store.getters['program/hasUnsavedProgramChanges'] ||
    store.getters['circuit/hasUnsavedCircuitChanges'] ||
    store.getters['prospect/hasUnsavedProspectChanges'] ||
    store.getters['trainer/hasUnsavedFeedbackChanges']
  ) {
    // There's a change unsaved. Prompt something
    store.commit(
      'modal/setContinueRoute',
      routeTo ? routeTo.fullPath : '/clients'
    );
    router.app.$bvModal.show('unsaved-changes-modal');
    return;
  }

  if (routeFrom.name !== null) {
    // Start the route progress bar.
    NProgress.start();
  }

  if (routeTo.name === 'logout') return next();

  // Check if auth is required on this route
  // (including nested routes).
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  // If auth isn't required for the route, just continue.
  if (!authRequired) return next();

  // Close admin panel to prevent breaking views on back
  if (store.getters['adminPanel/isAdminPanelActive']) {
    store.commit('adminPanel/setHideAdminPanel');
    store.commit('adminPanel/setPanelContent', null);
  }

  // If auth is required and the user is logged in...
  if (store.getters['auth/loggedIn']) {
    const currentUser = cloneDeep(store.state.auth.currentUser);
    // Validate the local user token...
    store.dispatch('auth/validate').then((validUser) => {
      // Then continue if the token still represents a valid user,
      // otherwise redirect to login.
      if (
        validUser &&
        isEqual(currentUser.roles, store.state.auth.currentUser.roles) &&
        ((currentUser.roles.isTrainer &&
          isEqual(
            currentUser.trainer.org_id,
            store.state.auth.currentUser.trainer.org_id
          ) &&
          isEqual(
            currentUser.trainer._id,
            store.state.auth.currentUser.trainer._id
          )) ||
          (currentUser.roles.isAdmin &&
            isEqual(
              currentUser.admin.org_id,
              store.state.auth.currentUser.admin.org_id
            ) &&
            isEqual(
              currentUser.admin._id,
              store.state.auth.currentUser.admin._id
            )))
      ) {
        // we have a valid user lets do a check on subscription.
        if (validUser.trainer && validUser.trainer.profile.subscribed) {
          store.dispatch('auth/hydrate');
        }
        if (validUser.trainer && !validUser.trainer.profile.subscribed) {
          return redirectToCheckout();
        }
        if (validUser.admin) {
          store.dispatch('auth/hydrate');
        }
      } else {
        store.dispatch('auth/logOut');
        return redirectToLogin();
      }
    });
    return next();
  }

  // If auth is required and the user is NOT currently logged in,
  // redirect to login.
  return redirectToLogin();

  function redirectToLogin() {
    // Pass the original route to the login component
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  function redirectToCheckout() {
    return next({
      name: 'register',
      query: { redirectFrom: routeTo.fullPath },
    });
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done();
              }
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // If we reach this point, continue resolving the route.
  next();
});

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Complete the animation of the route progress bar.
  NProgress.done();
  Vue.nextTick(() => window.userpilot && window.userpilot.reload());
});

export default router;
